import type { Action } from 'react-sweet-state';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { fireOperationalAnalytics } from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';
import { Action as AnalyticsAction, ActionSubject } from '@atlassian/proforma-utilities/analytics';

import {
	ErrorKeySet,
	ValidationFieldSet,
	type ValidationTargets,
} from '../../../../common/types/field-validation';
import type { FieldId, FormFields } from '../../../../common/types/form/form-fields';
import { fieldInvalidSelector } from '../../selectors/field-invalid';
import { fieldValidationErrorsSelector } from '../../selectors/field-validation-errors';
import { getAllVisibleFieldIdsSelector } from '../../selectors/get-all-visible-field-ids';
import type { FormRendererState } from '../../types';

/**
 * This action has been updated to return the field IDs that have validation or custom errors.
 */
export const getFieldIdsFailingValidation =
	(
		validationTargets?: ValidationTargets,
		formFields?: FormFields,
		createAnalyticsEvent?: CreateUIAnalyticsEvent,
	): Action<FormRendererState, void, FieldId[]> =>
	({ getState }) => {
		const state = getState();
		const effectiveStateForSelector = validationTargets
			? {
					...(formFields ? { ...state, formFields } : state),
					fieldValidation: { ...state.fieldValidation, validationTargets },
				}
			: { ...(formFields ? { ...state, formFields } : state) };

		const visibleFieldIds = getAllVisibleFieldIdsSelector(effectiveStateForSelector);

		const isRegexValidationEnforced = fg('proforma_enforce_regex_validation');

		let hasInvalidRegex = false;

		const failingFieldIds = visibleFieldIds.reduce<FieldId[]>((accFailingFieldIds, fieldId) => {
			if (fieldInvalidSelector(effectiveStateForSelector, { fieldId })) {
				accFailingFieldIds.push(fieldId);
				if (isRegexValidationEnforced && !hasInvalidRegex) {
					const validationErrors = fieldValidationErrorsSelector(effectiveStateForSelector, {
						fieldId,
					});
					if (validationErrors.some(({ errorKey }) => errorKey === ErrorKeySet.INVALID_REGEX)) {
						hasInvalidRegex = true;
					}
				}
			}

			return accFailingFieldIds;
		}, []);

		if (
			isRegexValidationEnforced &&
			hasInvalidRegex &&
			createAnalyticsEvent &&
			validationTargets?.fieldSet === ValidationFieldSet.ALL_FIELDS // we only want to fire this on request create/submit flows
		) {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				`${ActionSubject.FORM_VALIDATION} ${AnalyticsAction.FAILED}`,
				{ reason: 'invalidRegexPattern' },
			);
		}

		return failingFieldIds;
	};
